import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { faWallet } from '@fortawesome/free-solid-svg-icons';
import Table from '../Listing/Table';
import Accordion from '../NavTab/Accordion';
import { API_BASE_URL } from '../../api';

const formatAmount = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const TYPE_LABELS = {
    FACTURE: 'Vente',
    PAIEMENT: 'Paiement',
    SOLDE_INITIAL: 'Solde initial',
    AVOIR: 'Avoir',
};

// The full chronological statement of a patient's account — every entry that makes up
// "reste dû" (real sales/payments and manual adjustments alike), in the order the ledger
// unification work settled on (see PatientAccountService/OrderService). Adding a manual
// adjustment is triggered from the page-level FAB ("Solde initial"), not from here.
const PatientAccount = () => {
    const { id } = useParams();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/patients/${id}/account-entries`, { withCredentials: true })
            .then(({ data }) => setEntries(data))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [id]);

    let running = 0;
    const rows = entries.map((entry, i) => {
        running += entry.amount ?? 0;
        return { ...entry, _index: i + 1, _balance: running };
    });

    const columns = [
        { Header: '#', accessor: '_index', className: 'text-center' },
        {
            Header: 'Date',
            accessor: 'entryDate',
            Cell: (row) => new Date(row.entryDate).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', year: 'numeric' }),
        },
        {
            Header: 'Type',
            accessor: 'type',
            Cell: (row) => TYPE_LABELS[row.type] || row.type,
        },
        { Header: 'Libellé', accessor: 'label' },
        {
            Header: 'Montant',
            accessor: 'amount',
            className: 'text-end',
            Cell: (row) => (
                <span style={{ color: row.amount >= 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                    {row.amount >= 0 ? '+' : ''}{formatAmount(row.amount)} <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>dhs</span>
                </span>
            ),
        },
        {
            Header: 'Solde',
            accessor: '_balance',
            className: 'text-end',
            Cell: (row) => <strong>{formatAmount(row._balance)} <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>dhs</span></strong>,
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile).
    const renderEntryCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: '#111827' }}>{TYPE_LABELS[row.type] || row.type}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>
                        {new Date(row.entryDate).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                </div>

                {row.label && <span style={{ fontSize: 11.5, color: '#6b7280' }}>{row.label}</span>}

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: row.amount >= 0 ? '#dc2626' : '#16a34a' }}>
                        {row.amount >= 0 ? '+' : ''}{formatAmount(row.amount)} <span style={{ fontSize: 10.5, fontWeight: 400, color: '#9ca3af' }}>dhs</span>
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111827' }}>
                        {formatAmount(row._balance)} <span style={{ fontSize: 10.5, fontWeight: 400, color: '#9ca3af' }}>dhs</span>
                    </span>
                </div>
            </div>
        </div>
    );

    return (
        <Accordion
            id="patientAccount"
            parent="patient_information_container"
            title="Compte"
            icon={faWallet}
            count={entries.length}
            flush
        >
                <Table
                    mode="client"
                    data={rows}
                    loading={loading}
                    pageSize={rows.length || 1}
                    emptyMessage="Aucun mouvement sur ce compte"
                    columns={columns}
                    enableCardView={true}
                    renderCard={renderEntryCard}
                />
        </Accordion>
    );
};

export default PatientAccount;
